// Implements: F-MCK-006, F-UIX-009, F-UIX-010, F-UIX-013
/**
 * The rename of a gallery card, in a dialog (the card menu's "Renombrar").
 *
 * The server trims and bounds the name and answers with its own sentence when it refuses (a role
 * that may not rename, say): the dialog then stays open with that sentence and what was typed, and
 * nothing is retried (`rename` is sent once, `retry: false`). The container is mounted with the
 * mockup's id as its key, so a draft or a late answer never lands on another mockup (checklist 14),
 * and a ref stops a double press from sending twice.
 */

import { useRef, useState } from "react";
import type { ReactElement } from "react";

import { useTranslation } from "../../../shared/i18n";
import { Button } from "../../../shared/ui/atoms/Button";
import { Input } from "../../../shared/ui/atoms/Input";
import { Field, describedBy } from "../../../shared/ui/molecules/Field";
import { userMessage } from "../../../shared/ui/molecules/userMessage";
import { Dialog } from "../../../shared/ui/organisms/Dialog";
import { useMockupActions } from "../hooks/useMockupActions";

const NAME_FIELD_ID = "gallery-rename";

export interface RenameMockupContainerProps {
  readonly mockupId: string;
  readonly currentName: string;
  readonly onClose: () => void;
}

export function RenameMockupContainer({ mockupId, currentName, onClose }: RenameMockupContainerProps): ReactElement {
  const { t } = useTranslation();
  const { rename } = useMockupActions();
  const [name, setName] = useState(currentName);
  const [pending, setPending] = useState(false);
  const [refusal, setRefusal] = useState<string | undefined>(undefined);
  const sending = useRef(false);

  const save = async (): Promise<void> => {
    const trimmed = name.trim();
    if (sending.current || trimmed === "") return;
    sending.current = true;
    setPending(true);
    setRefusal(undefined);
    const failure = await rename(mockupId, trimmed);
    sending.current = false;
    setPending(false);
    if (failure === null) onClose();
    else setRefusal(userMessage(failure, t("gallery.rename.failed")));
  };

  return (
    <Dialog
      open
      title={t("gallery.rename.title")}
      onClose={onClose}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            {t("common.action.cancel")}
          </Button>
          <Button
            variant="primary"
            loading={pending}
            disabled={name.trim() === ""}
            onClick={() => {
              void save();
            }}
          >
            {t("gallery.rename.save")}
          </Button>
        </>
      }
    >
      <form
        noValidate
        onSubmit={(event) => {
          event.preventDefault();
          void save();
        }}
      >
        <Field id={NAME_FIELD_ID} label={t("gallery.rename.label")} error={refusal}>
          <Input
            id={NAME_FIELD_ID}
            value={name}
            onChange={setName}
            invalid={refusal !== undefined}
            describedBy={describedBy(NAME_FIELD_ID, { error: refusal })}
            readOnly={pending}
          />
        </Field>
      </form>
    </Dialog>
  );
}
